import { Popover, PopoverContent, PopoverTrigger } from '@/components/common/PopoverCustom';
import { buttonAnimation } from '@/lib/animation';
import getMediaUrl from '@/lib/utils';
import Toast from '@flyer/components/toast/toast';
import time from '@flyer/utils/time';
import { m } from 'framer-motion';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import playIcon from './ic-pause.png';
import pauseIcon from './ic-play.png';
import tapScript from './tapscript.png';
import playIconSpeaking from './ic-play-2.png';
import pauseIconSpeaking from './ic-pause-2.png';

type ExamAudioProps = {
  autoplay?: boolean;
  src: string;
  isShowButtonTapScript?: boolean;
  onClickButtonTapScript?: () => void;
  isDesignSpeaking?: boolean;
  isNotLoadFromStore?: boolean;
  wrapClassName?: string;
  isCanPlaying?: boolean;
  onPlayStateChange?: (isPlaying: boolean) => void;
};

export default function FlyerAudio({
  src,
  autoplay = true,
  isShowButtonTapScript = false,
  onClickButtonTapScript,
  isDesignSpeaking = false,
  isNotLoadFromStore = false,
  wrapClassName,
  isCanPlaying = true,
  onPlayStateChange,
}: ExamAudioProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation('misc');

  const [{ isPlaying, duration, currentTime, isLoaded }, setInfo] = useState({
    duration: 0,
    currentTime: 0,
    isPlaying: autoplay,
    isLoaded: false,
  });

  const [isShowSelectSpeed, setIsShowSelectSpeed] = useState<boolean>(false);

  const togglePlay = useCallback(async () => {
    if (!isCanPlaying) {
      return Toast.error(t('ielts:DoSpeaking.not_playing_audio'));
    }
    if (!audioRef.current) {
      return null;
    }
    if (audioRef.current?.paused) {
      await audioRef.current?.play();
      onPlayStateChange?.(true);
      return setInfo((prev) => ({ ...prev, isPlaying: true }));
    }
    void audioRef.current?.pause();
    onPlayStateChange?.(false);
    return setInfo((prev) => ({ ...prev, isPlaying: false }));
  }, [isCanPlaying, t, onPlayStateChange]);

  useEffect(() => {
    const dataAudio = isNotLoadFromStore ? src : getMediaUrl(src, 'audio');
    if (dataAudio) {
      if (!audioRef.current) {
        audioRef.current = new Audio(dataAudio);
        return;
      }
      audioRef.current.src = isNotLoadFromStore ? src : getMediaUrl(src, 'audio');
      audioRef.current.volume = 1;
    }
  }, [isNotLoadFromStore, src]);

  const handleMetaDataAudioLoaded = useCallback((e: Event) => {
    // handle  audio not load
    if (e.target) {
      const audioElement = e.target as HTMLAudioElement;
      if (audioElement.duration === Number.POSITIVE_INFINITY) {
        setInfo((prev) => ({
          ...prev,
          isLoaded: true,
        }));
      } else {
        setInfo((prev) => ({
          ...prev,
          isLoaded: true,
          currentTime: audioElement.currentTime,
          duration: audioElement.duration,
        }));
      }
    }
  }, []);

  const handleDurationChange = useCallback((e: Event) => {
    if (e.target) {
      const audioElement = e.target as HTMLAudioElement;
      if (audioElement.duration !== Number.POSITIVE_INFINITY) {
        setInfo((prev) => ({
          ...prev,
          duration: audioElement.duration,
        }));
      }
    }
  }, []);

  const handleEnded = useCallback(() => {
    setInfo((prev) => ({
      ...prev,
      isPlaying: false,
    }));
  }, []);

  const handlerSetPlayBackRate = useCallback((speed: number) => {
    if (audioRef?.current?.playbackRate && speed) {
      audioRef.current.playbackRate = speed;
      setIsShowSelectSpeed(false);
    }
  }, []);

  const handlerShowSpeed = useCallback(() => {
    void setIsShowSelectSpeed((cur) => !cur);
  }, []);

  // handler click outside close select speed
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
        setIsShowSelectSpeed(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    audioRef.current?.addEventListener('loadedmetadata', handleMetaDataAudioLoaded);
    audioRef.current?.addEventListener('ended', handleEnded);
    audioRef.current?.addEventListener('pause', handleEnded);
    audioRef.current?.addEventListener('durationchange', handleDurationChange);
    return () => {
      audioRef.current?.removeEventListener('loadedmetadata', handleMetaDataAudioLoaded);
      audioRef.current?.removeEventListener('ended', handleEnded);
      audioRef.current?.removeEventListener('pause', handleEnded);
      audioRef.current?.removeEventListener('durationchange', handleDurationChange);
    };
  }, [handleDurationChange, handleEnded, handleMetaDataAudioLoaded]);

  useEffect(() => {
    const handleTimeUpdateLoaded = (e: Event) => {
      if (e.target) {
        const audioElement = e.target as HTMLAudioElement;
        setInfo((prev) => ({ ...prev, currentTime: audioElement.currentTime }));
      }
    };
    audioRef.current?.addEventListener('timeupdate', handleTimeUpdateLoaded);
    return () => {
      audioRef.current?.removeEventListener('timeupdate', handleTimeUpdateLoaded);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current?.pause();
      }
    };
  }, []);

  useEffect(() => {
    // auto play
    if (autoplay && audioRef.current && isLoaded) {
      void togglePlay();
    }
  }, [autoplay, togglePlay, isLoaded]);

  const listSpeed = useMemo(() => {
    return [
      {
        key: 0.25,
        title: '0.25',
      },
      {
        key: 0.5,
        title: '0.5',
      },
      {
        key: 0.75,
        title: '0.75',
      },
      {
        key: 1,
        title: `1 ( ${t('audio_component.normal_speed')} )`,
      },
      {
        key: 1.25,
        title: '1.25',
      },
      {
        key: 1.5,
        title: '1.5',
      },
      {
        key: 1.75,
        title: '1.75',
      },
    ];
  }, [t]);

  const renderIconPlayPause = useMemo(() => {
    if (isPlaying) {
      return isDesignSpeaking ? playIconSpeaking : playIcon;
    }
    return isDesignSpeaking ? pauseIconSpeaking : pauseIcon;
  }, [isDesignSpeaking, isPlaying]);

  return (
    <div className="relative" ref={componentRef}>
      <div
        className={twMerge(
          ' border-solid border-[1.18822px] ',
          'flex items-center gap-2 text-white  justify-between',
          isDesignSpeaking
            ? 'bg-[#75B9FC] border-[#75B9FC] rounded-[7px]'
            : 'bg-[#BDB0FF] border-[#E7D5FF] rounded-[clamp(39.6074px,min(1.6vh,0.5vw),1rem)]',
          wrapClassName,
        )}
        style={
          isDesignSpeaking
            ? {
                boxShadow: '0px 4px 0px 0px #0152A7',
              }
            : undefined
        }
      >
        <div
          className={twMerge(
            'flex items-center gap-[4px] py-[2px] px-[8px] md:gap-2  xl:px-3 xl:py-1 justify-center w-full',
            isDesignSpeaking ? 'h-[40px]' : '2xl:h-[32px]',
          )}
        >
          <div
            className={twMerge(
              'flex',
              isDesignSpeaking ? 'absolute z-10 left-[-16px] top-[-11px]' : '',
            )}
          >
            <m.button type="button" whileTap={buttonAnimation.whileTap} onClick={togglePlay}>
              <Image
                src={renderIconPlayPause}
                alt="Play"
                className={twMerge(isDesignSpeaking ? 'w-[66px] h-[66px]' : 'w-[19px]')}
              />
            </m.button>
          </div>
          <time
            className={twMerge(
              'font-bold flex flex-nowrap md:min-w-[65px] whitespace-nowrap',
              isDesignSpeaking ? 'pl-[65px] pr-[30px] text-[13px]' : 'text-[6px] md:text-[10px]',
            )}
          >
            {time.duration(currentTime, 'seconds').format('mm:ss')} /{' '}
            {time.duration(duration, 'seconds').format('mm:ss')}
          </time>
          <Slider
            step={0.2}
            value={(currentTime / duration) * 100}
            allowCross
            style={{
              padding: '3px 0',
            }}
            onChange={(value) => {
              if (typeof value === 'number') {
                const newTimePosition = (value / 100) * duration;
                if (audioRef?.current?.currentTime) {
                  audioRef.current.currentTime = newTimePosition;
                }
                setInfo((prev) => ({
                  ...prev,
                  currentTime: newTimePosition,
                }));
              }
            }}
            styles={{
              tracks: {
                background: `linear-gradient(180deg, #FFDC30 0%, #FFDD7C 51.5%, #FFC734 55%, #FFB524 100%)`,
                height: '8px',
                borderRadius: 3,
              },
              track: {
                background: 'transparent',
              },
              handle: {
                borderColor: '#FFDC30',
                display: isPlaying ? 'block' : 'none',
                height: 12,
                width: 12,
                marginLeft: 0,
                marginTop: -3,
                backgroundColor: '#FFDC30',
              },
              rail: {
                backgroundColor: '#0C1F54',
                height: '8px',
              },
            }}
          />
          <Popover open={isShowSelectSpeed}>
            {() => {
              return (
                <>
                  <PopoverTrigger
                    className={twMerge(
                      'outline-none',
                      isDesignSpeaking ? 'flex w-[30px] justify-end' : '',
                    )}
                  >
                    <div
                      aria-hidden
                      className="flex flex-col gap-[2px] cursor-pointer pr-[6px]"
                      onClick={handlerShowSpeed}
                    >
                      <div className="w-[3px] h-[3px] bg-white" />
                      <div className="w-[3px] h-[3px] bg-white" />
                      <div className="w-[3px] h-[3px] bg-white" />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="outline-none overflow-x-hidden z-[100]">
                    <div
                      data-test-id="select-speed"
                      className={twMerge(
                        'bg-[#BDB0FF] border-[1px] border-solid  rounded-[8px] lg:rounded-[16px] w-[150px] lg:w-[172px] text-center mb-[8px]',
                        isDesignSpeaking
                          ? 'bg-[#75B9FC] border-[#75B9FC]'
                          : 'bg-[#BDB0FF] border-[#E7D5FF]',
                      )}
                    >
                      <span className="text-[#54638C] text-[8px] lg:text-[10px] font-bold w-full text-center">
                        {t('audio_component.audio_speed')}
                      </span>

                      <div className="flex flex-col w-full justify-center items-center">
                        {listSpeed.map((sp) => (
                          <div
                            aria-hidden
                            key={sp.key}
                            className={twMerge(
                              'text-[10px] lg:text-[12px] font-bold leading-[15px] lg:leading-[30px] cursor-pointer',
                              audioRef?.current?.playbackRate === sp.key
                                ? isDesignSpeaking
                                  ? 'bg-[#75B9FC] w-full'
                                  : 'text-[#0C1F54] bg-[#A492FF] w-full'
                                : isDesignSpeaking
                                  ? 'bg-[#75B9FC] text-white'
                                  : 'bg-[#BDB0FF] text-white',
                            )}
                            onClick={() => handlerSetPlayBackRate(sp.key)}
                          >
                            {sp.title}
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </>
              );
            }}
          </Popover>

          {isShowButtonTapScript && onClickButtonTapScript && (
            <div
              className="md:max-w-[97px] w-[22%] cursor-pointer"
              aria-hidden
              onClick={onClickButtonTapScript}
            >
              <Image src={tapScript} alt="flyer" className="w-full h-full md:max-h-[25px]" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
